// Search, sort and date-range controls for blog-style sections (used above the post list in UxWorkspace).
// Helpers: window.BlogFinder.apply(posts, { query, sort, from, to }) -> filtered + sorted posts.
// A post's date is `date` (YYYY-MM-DD) and falls back to the last-updated day.
(function () {
  const { Box } = MaterialUI;

  const SORTS = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'az', label: 'A to Z' }
  ];
  const RANGES = [
    { value: '', label: 'Any time' },
    { value: '30', label: 'Last 30 days' },
    { value: '365', label: 'Last year' },
    { value: 'custom', label: 'Custom range' }
  ];

  const dayOf = p => p.date || (p.updatedAt ? String(p.updatedAt).slice(0, 10) : '');
  const isoDaysAgo = n => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

  function blockText(blocks = []) {
    return blocks.map(b => [b.x, b.sub, b.c, b.code, (b.items || []).map(i => (typeof i === 'string' ? i : blockText([i]))).join(' ')].filter(Boolean).join(' ')).join(' ');
  }

  // Search ignores case and diacritics, so "iasi" finds "Iași" and "stefan" finds "Ștefan" (ă â î ș ț, and the cedilla forms ş ţ)
  const fold = t => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  function apply(posts, { query = '', sort = 'newest', from = '', to = '' }) {
    const words = fold(query).split(/\s+/).filter(Boolean);
    const list = posts.filter(p => {
      const d = dayOf(p);
      if (from && (!d || d < from)) return false;
      if (to && (!d || d > to)) return false;
      if (!words.length) return true;
      const hay = fold([p.title, p.kind, p.description, (p.tags || []).join(' '), blockText(p.blocks)].join(' '));
      return words.every(w => hay.includes(w));
    });
    if (sort === 'az') return list.sort((a, b) => a.title.localeCompare(b.title));
    const dir = sort === 'oldest' ? 1 : -1;
    return list.sort((a, b) => dir * (dayOf(a).localeCompare(dayOf(b)) || a.title.localeCompare(b.title)));
  }

  function BlogFinder({ state, onChange, total, shown }) {
    const { SearchBar, Select, TextField, Button, GoogleIcon, FilterMenu } = window.Components;
    const compact = window.useCompactLayout();
    const { query, sort, range, from, to } = state;
    const set = patch => onChange({ ...state, ...patch });
    const pickRange = value => {
      if (value === '30' || value === '365') set({ range: value, from: isoDaysAgo(Number(value)), to: '' });
      else if (value === 'custom') set({ range: value });
      else set({ range: '', from: '', to: '' });
    };
    const active = query || range;
    const searchBar = <SearchBar placeholder="Search posts…" value={query} onChange={e => set({ query: e.target.value })} onClear={() => set({ query: '' })} />;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 1 }}>
        {compact ? (
          // Phones: the filter button sits next to the search; sort and date range are chosen in its menu
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{searchBar}</Box>
            <FilterMenu
              count={(sort !== 'newest' ? 1 : 0) + (range ? 1 : 0)}
              onClear={() => onChange({ ...state, sort: 'newest', range: '', from: '', to: '' })}
              groups={[
                { title: 'Sort by', options: SORTS.map(o => ({ ...o, selected: o.value === sort })), onPick: v => set({ sort: v }) },
                { title: 'Date range', options: RANGES.map(o => ({ ...o, selected: o.value === range })), onPick: pickRange }
              ]}
            />
          </Box>
        ) : (
          <>
            {searchBar}
            <Select label="Sort by" size="small" value={sort} onChange={e => set({ sort: e.target.value })} options={SORTS} />
            <Select label="Date range" size="small" value={range} onChange={e => pickRange(e.target.value)} options={RANGES} />
          </>
        )}
        {range === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={e => set({ from: e.target.value })} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={e => set({ to: e.target.value })} />
          </Box>
        )}
        {active && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box component="span" aria-live="polite" sx={{ typography: 'caption', color: 'text.secondary' }}>{shown} of {total} posts</Box>
            <Button variant="text" size="small" icon={<GoogleIcon name="filter_alt_off" size={18} />} onClick={() => onChange({ query: '', sort, range: '', from: '', to: '' })}>Clear</Button>
          </Box>
        )}
      </Box>
    );
  }

  window.Components = window.Components || {};
  window.Components.BlogFinder = BlogFinder;
  window.BlogFinder = { apply, dayOf, defaultState: { query: '', sort: 'newest', range: '', from: '', to: '' } };
})();
